import React, { useState, useEffect } from "react";
import { SnackbarProvider } from "notistack";
import { ThemeProvider } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";
import analytics, { setAnalyticsContext } from "core/utils/analytics";
import { createTheme } from "core/utils/theme";
import { useQuery } from "@apollo/client";
import { ArenaQuery } from "core/queries";
import LoadingScreen from "core/components/LoadingScreen";
import ErrorScreen from "core/components/ErrorScreen";
import ViewerProvider from "core/components/ViewerProvider";
import ArenaProvider from "core/components/ArenaProvider";
import AppRoutes from "arena/components/AppRoutes";
import AppHelmet from "arena/components/AppHelmet";
import CookieBanner from "arena/components/CookieBanner";
import CastProvider from "core/components/CastProvider";
import RetargetPixels from "core/components/RetargetPixels";
import PlayerFullscreenProvider from "core/components/PlayerFullscreenProvider";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import ArenaNotFound from "arena/components/ArenaNotFound/ArenaNotFound";
import WelcomeDialog from "arena/components/WelcomeDialog";

const anchorOrigin = {
  vertical: "top",
  horizontal: "center",
};

export default function App() {
  const { data, loading, error } = useQuery(ArenaQuery);
  const [showWelcomeDialog, setShowWelcomeDialog] = useState(false);

  const arena = data?.arena;

  // Check if we should show welcome dialog for specific arena
  useEffect(() => {
    if (arena && arena.id === "kC_Tapv138HxC0o0q5PrUvB4t5HmJSc2ZNfTAQmtkw0") {
      const welcomeDialogShown = localStorage.getItem(
        "ginastica-welcome-dialog-shown"
      );
      if (!welcomeDialogShown) {
        setShowWelcomeDialog(true);
      }
    }
  }, [arena]);

  if (loading) return <LoadingScreen />;
  if (error) return <ErrorScreen error={error} />;

  analytics.initialize(arena);
  setAnalyticsContext({ surface: "arena" });

  const theme = createTheme(arena?.theme);

  const handleCloseWelcomeDialog = () => {
    setShowWelcomeDialog(false);
    localStorage.setItem("ginastica-welcome-dialog-shown", "true");
  };

  if (!arena) {
    return (
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <ArenaNotFound />
      </ThemeProvider>
    );
  }

  if (!arena.available) {
    if (!arena.published) {
      return (
        <ThemeProvider theme={theme}>
          <CssBaseline />
          <ErrorScreen icon={AccessTimeIcon} title="Coming soon" code={404} />
        </ThemeProvider>
      );
    }

    return (
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <ErrorScreen title="Arena not available" code={404} />
      </ThemeProvider>
    );
  }

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <AppHelmet arena={arena} />
      <SnackbarProvider
        maxSnack={1}
        anchorOrigin={anchorOrigin}
        autoHideDuration={2000}
      >
        <ArenaProvider value={arena}>
          <ViewerProvider useViewerLanguage={arena.multilingual}>
            <PlayerFullscreenProvider>
              <CastProvider>
                <AppRoutes />
                <RetargetPixels eventType="PageView" />
              </CastProvider>
            </PlayerFullscreenProvider>
            <CookieBanner arena={arena} />
            <WelcomeDialog
              open={showWelcomeDialog}
              onClose={handleCloseWelcomeDialog}
            />
          </ViewerProvider>
        </ArenaProvider>
      </SnackbarProvider>
    </ThemeProvider>
  );
}
